<script setup lang="ts">
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/vue";
import type { Branches } from "~/types/directus-collections.d.ts";
const route = useRoute();
const router = useRouter();

useHead({
  title: "Groups",
});

const isMapView = ref(true);

// Pagination
const pageSize = 7;
const currentPage = computed({
  get: () => (typeof route.query.page === "string" && !isNaN(parseInt(route.query.page, 10)) ? parseInt(route.query.page, 10) : 1),
  set: (i) => router.replace({ query: { ...route.query, page: i } }),
});

// Search Filter
const searchFilter = computed({
  get: () => (typeof route.query.search === "string" ? route.query.search : undefined),
  set: (s) => router.replace({ query: { ...route.query, search: s, page: 1 } }),
});

const searchValue = ref(searchFilter.value || "");
const searchField = ref<HTMLElement>();
function submitSearch() {
  searchFilter.value = searchValue.value;
  searchField.value?.focus();
}
function clearSearch() {
  searchFilter.value = searchValue.value = "";
  searchField.value?.focus();
}

// Category filter
const categoryFilter = computed({
  get: () => (typeof route.query.category === "string" ? route.query.category : undefined),
  set: (v) => {
    router.replace({ query: { ...route.query, category: v, page: 1 } });
  },
});
const categories = [
  "Coastal conservation",
  "Bushland & Heritage",
  "Community",
  "Flora",
  "Islands & Heritage",
  "Adventure Sites Conservation",
  "Wildlife education and conservation",
  "Wildlife rescue and rehabilitation",
];
const categoryValue = ref(categoryFilter.value || "");

function setCategory() {
  categoryFilter.value = categoryValue.value;
}

function clearCategory() {
  categoryFilter.value = categoryValue.value = "";
}

// Donations filter
const donationsFilter = computed(() => route.query.donations === "1");

function clearDonationsFilter() {
  const { donations, ...rest } = route.query;
  router.replace({ query: { ...rest, page: 1 } });
}

// Filter for query

const filter = computed(() => {
  const filter: Record<string, unknown> = {
    status: { _eq: "published" },
  };
  if (categoryFilter.value && categoryFilter.value.length) {
    filter.category = { _eq: categoryFilter.value };
  }
  if (isMapView.value) {
    filter.location = { _neq: null };
  }
  if (donationsFilter.value) {
    filter.accept_donation = { _eq: true };
  }
  return filter;
});

const { data: totalCount } = useAsyncData(
  () =>
    useDirectus(() => ({
      path: `/count-collections/branches`,
      method: "GET",
      params: {
        filter: filter.value,
        search: searchFilter.value || "",
      },
    })).then((results: any) => results.count),
  {
    watch: [currentPage, searchFilter, filter, donationsFilter],
    default: () => 0,
  }
);

// Fetching branch data
const { data: branches, pending } = await useAsyncData(
  () =>
    useDirectus(
      readItems("branches", {
        fields: ["*", "count(branch_members)"],
        filter: filter.value,
        sort: "title",
        search: searchFilter.value || "",
        limit: isMapView.value ? -1 : pageSize,
        page: isMapView.value ? 1 : currentPage.value,
      })
    ),
  {
    watch: [currentPage, searchFilter, filter, isMapView],
    default: () => [] as Branches[],
  }
);
</script>

<template>
  <div class="bg-gray">
    <CommonPageBanner :local-asset="true" image="/groups-banner.jpg" size="small">
      <p class="font-script text-xl md:text-2xl xl:text-3xl">Discover, join, donate</p>
      <h1 class="text-4xl font-semibold md:text-6xl xl:text-banner">Wildcare Groups</h1>
    </CommonPageBanner>
    <div class="bg-primary">
      <CommonContainer>
        <div class="flex flex-col items-center justify-between space-y-8 py-6 text-white md:flex-row md:space-x-8 md:space-y-0">
          <form class="relative max-w-[300px] grow xl:max-w-[400px]" @submit.prevent="submitSearch">
            <input
              ref="searchField"
              v-model="searchValue"
              name="search"
              type="text"
              placeholder="Begin your search"
              class="w-full rounded-full bg-white/5 py-3 pl-8 pr-12 text-white placeholder:text-white"
              @keyup.esc="clearSearch"
            />
            <div class="absolute inset-y-0 right-3 flex">
              <button type="submit" class="flex w-9 items-center justify-center">
                <font-awesome-icon icon="fa-solid fa-search" />
              </button>
              <button v-if="searchFilter" class="flex w-9 items-center justify-center" @click="clearSearch">
                <font-awesome-icon icon="fa-solid fa-close" />
              </button>
            </div>
          </form>
          <div class="mx-auto flex h-8 items-center gap-8 border-x-2 border-x-white/50 px-8 font-semibold text-white">
            <button :class="[isMapView ? 'text-secondary' : '']" @click="isMapView = true">Map</button>
            <button :class="[isMapView ? '' : 'text-secondary']" @click="isMapView = false">List</button>
          </div>
          <Listbox v-model="categoryValue">
            <div class="relative mt-1 flex max-w-[300px] grow items-center justify-end gap-4 xl:max-w-[400px]">
              <button
                v-if="categoryValue"
                class="mx-2 flex w-9 items-center justify-center gap-x-2 text-sm uppercase"
                @click="clearCategory"
              >
                <font-awesome-icon icon="fa-solid fa-close" size="xl" />
                clear
              </button>
              <ListboxButton class="flex w-fit items-center gap-x-3 rounded-full bg-white/5 px-8 py-5 text-sm uppercase md:text-base">
                <font-awesome-icon icon="fa-solid fa-angle-down" />
                {{ categoryValue ? categoryValue : "Pick Category" }}
              </ListboxButton>
              <Transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
                <ListboxOptions class="absolute z-50 mt-1 w-full overflow-auto rounded-md bg-primary">
                  <ListboxOption
                    v-for="category in categories"
                    :key="category"
                    :value="category"
                    class="bg-white/5 p-2 text-sm uppercase transition hover:cursor-pointer hover:bg-secondary hover:text-black md:text-base"
                    @click="setCategory()"
                  >
                    {{ category }}
                  </ListboxOption>
                </ListboxOptions>
              </Transition>
            </div>
          </Listbox>
        </div>
      </CommonContainer>
    </div>
    <Transition
      enter-active-class="transition-all duration-200 ease-out overflow-hidden"
      enter-from-class="max-h-0 opacity-0"
      enter-to-class="max-h-20 opacity-100"
      leave-active-class="transition-all duration-200 ease-in overflow-hidden"
      leave-from-class="max-h-20 opacity-100"
      leave-to-class="max-h-0 opacity-0"
    >
      <div v-if="donationsFilter" class="bg-tertiary">
        <CommonContainer>
          <div class="flex items-center justify-between gap-4 py-3 text-sm font-semibold text-accentDark">
            <span>
              <font-awesome-icon icon="fa-solid fa-hand-holding-dollar" class="mr-2" />
              Showing groups that are currently accepting donations
            </span>
            <button
              class="flex shrink-0 items-center gap-2 self-center rounded-full bg-primary px-4 py-2 text-xs uppercase text-white hover:underline"
              @click="clearDonationsFilter"
            >
              Remove filter
            </button>
          </div>
        </CommonContainer>
      </div>
    </Transition>
    <div v-if="pending" class="flex items-center justify-center py-[--y-margin]">
      <font-awesome-icon class="mr-3 text-primary" icon="fa-solid fa-spinner" size="xl" spin-pulse></font-awesome-icon>
    </div>
    <div v-else>
      <div v-if="isMapView" class="relative py-8 xl:py-16">
        <ClientOnly fallback-tag="div">
          <div class="flex flex-col items-center gap-8 lg:flex-row lg:items-start lg:gap-0">
            <BranchMapList class="grow basis-1/2 px-[--x-margin] lg:px-12" :branches="branches" />
            <BranchMap class="order-first grow basis-1/2 lg:sticky lg:top-8 lg:order-none xl:top-16" :branches="branches" />
          </div>
          <template #fallback>
            <!-- this will be rendered on server side -->
            <font-awesome-icon class="mr-3 text-primary" icon="fa-solid fa-spinner" size="xl" spin-pulse></font-awesome-icon>
          </template>
        </ClientOnly>
      </div>
      <div v-else>
        <CommonContainer class="relative py-[--y-margin]">
          <div v-if="branches.length">
            <CommonCommentTag class="branch-page" tag-class="branch-page"> Join as many groups as you like </CommonCommentTag>
            <div class="space-y-7">
              <BranchPreview v-for="branch in branches" :key="branch.id" :branch="branch" />
            </div>
            <CommonPagination
              v-if="totalCount > pageSize"
              v-model:page="currentPage"
              :page-size="pageSize"
              :total-count="totalCount"
              :disabled="pending"
              class="mt-8 xl:mt-16"
            ></CommonPagination>
          </div>
          <div v-else class="text-2xl">
            <h3>No groups match your search.</h3>
          </div>
        </CommonContainer>
      </div>
    </div>
  </div>
</template>
